import { useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Formik, Form, Field } from 'formik'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import classNames from 'classnames'

import { Trans } from 'i18n-web/i18next'
import { Subtitle } from 'components/Molecules/Purchase'
import Button from 'components/Atoms/Button'
import Transition from 'components/Atoms/Transition'
import Text from 'components/Atoms/Text'
import TextDivisor from 'components/Atoms/TextDivisor'
import AppleSignInButton from 'components/Atoms/AppleSignInButton'
// import FacebookButton from 'components/Atoms/FacebookButton'
import { TextField, PhoneField } from 'components/Molecules/Form'
import { PhoneNumberTooltip } from 'components/Molecules/PayBySignage/Purchase'

import validationSchema from './validationSchema'

const shakeNextButton = keyframes`
  2%, 18% {
    transform: translate3d(-2px, 0, 0);
  }

  4%, 16% {
    transform: translate3d(2px, 0, 0);
  }

  6%, 10%, 14% {
    transform: translate3d(-2px, 0, 0);
  }

  8%, 12% {
    transform: translate3d(2px, 0, 0);
  }

  18.1% {
    transform: translate3d(0px, 0, 0);
  }
`

const Component = styled(Form)`
  text-align: center;
`

const Tooltip = styled.div`
  margin-top: -14px;
`

const PhoneNumberBox = styled.div`
  display: flex;
`

const PasswordTitle = styled.div`
  margin-top: 23px;
`

const PasswordLabel = styled(Text)`
  margin-top: -14px;
  margin-left: 18px;
  margin-bottom: 12px;
`

const PasswordDivisor = styled(TextDivisor)`
  margin-top: 40px;
  margin-bottom: 0;
`

const PasswordBox = styled.div`
  display: flex;
  align-items: flex-start;
`

const PasswordField = styled(TextField)`
  margin-bottom: 0;
  flex: 1;
`

const PasswordButton = styled(Button)`
  margin-top: 25px;
  margin-left: 8px;
  height: 44px;
`

const PhoneNumber = styled(PhoneField)`
  flex: 1;
`

const NextButton = styled(Button)`
  margin-bottom: 15px;
  margin-left: 8px;
  margin-top: 4px;
  height: 44px;
  &.shakeButton {
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    animation: ${shakeNextButton} 2s linear infinite;
  }
`

const NativeButton = styled.div`
  margin-top: 10px;
`

const Screen = props => {
  const {
    currentCountry,
    error,
    handleSubmit,
    initialValues,
    onClearAuthentication,
    onFocusField,
    passwordRef,
    loginType,
    application,
    enableLogin,
    router,
    isQRCode,
    resetFormState,
    phoneNumberRef,
    onAppleButtonClick,
    submitOnKeyUp,
    signInRef,
    hasNextButton,
    shakeButton,
    setShakeButton,
    t,
  } = props

  const [showAlert, setShowAlert] = useState(false)

  const onPasswordFocus = () => {
    if (!showAlert) {
      setShowAlert(true)
    }
  }

  return (
    <Formik
      ref={signInRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={() => validationSchema(currentCountry)}
      render={props => {
        const {
          isSubmitting,
          setSubmitting,
          setFieldError,
          setStatus,
          values,
          validateForm,
          isValid,
        } = props

        return (
          <Component>
            <PhoneNumberBox id="phoneNumberField">
              <Field
                inputRef={phoneNumberRef}
                component={PhoneNumber}
                disabled={isSubmitting && !error}
                name="phoneNumber"
                validateOnFocus
                onChange={resetFormState}
                onKeyUp={() => {
                  submitOnKeyUp(isValid, isSubmitting)
                }}
                onBlur={() => {
                  submitOnKeyUp(isValid, isSubmitting)
                }}
                onFocus={() => {
                  if (isEmpty(values.phoneNumber)) {
                    validateForm()
                  }
                  onFocusField(setSubmitting, setStatus)
                }}
              />
              {isQRCode && hasNextButton && (
                <NextButton
                  className={classNames({ shakeButton: shakeButton })}
                  disabled={(isSubmitting && !error) || enableLogin}
                  onClick={() => setShakeButton(false)}
                  type="submit">
                  {t('payBySignage.signIn.form.buttons.next').toUpperCase()}
                </NextButton>
              )}
            </PhoneNumberBox>
            {isQRCode && (
              <Tooltip>
                <PhoneNumberTooltip />
              </Tooltip>
            )}
            <Transition show={loginType === 'password'}>
              <PasswordTitle>
                <Subtitle>
                  <Trans i18nKey={'payBySignage.useMyAccount'} count={3} />
                </Subtitle>
                <PasswordLabel className="small">
                  {t('payBySignage.loginIsOptionalStep')}
                </PasswordLabel>
              </PasswordTitle>
              <PasswordBox>
                <Field
                  component={PasswordField}
                  disabled={isSubmitting && !error}
                  inputRef={passwordRef}
                  autocomplete="off"
                  label={t('payBySignage.signIn.form.password.label')}
                  name="password"
                  onFocus={() => {
                    onPasswordFocus()
                    onClearAuthentication()
                    setSubmitting(false)
                    setStatus(null)
                    setFieldError('password', false)
                  }}
                  type="password"
                />
                <PasswordButton
                  className="highlight"
                  disabled={isSubmitting && !error}
                  type="submit">
                  {t('payBySignage.signIn.form.buttons.submit').toUpperCase()}
                </PasswordButton>
              </PasswordBox>
              <PasswordDivisor
                textClassName="extraBold quaternary"
                className="quaternary"
                lineWidth={2}
                text={t('payBySignage.continueAsAGuest').toUpperCase()}
              />
            </Transition>

            {loginType === 'apple' && (
              <NativeButton>
                <AppleSignInButton
                  skipLoginFlow
                  application={application}
                  onClick={() => onAppleButtonClick()}
                  callbackRedirection={router.asPath}
                  className="full">
                  {t('common.button.continueWithApple')}
                </AppleSignInButton>
              </NativeButton>
            )}
            {/* {loginType === 'facebook' && (
              <NativeButton>
                <FacebookButton showErrorMessage className="full">
                  {t('common.button.continueWithFacebook')}
                </FacebookButton>
              </NativeButton>
            )} */}
          </Component>
        )
      }}
    />
  )
}

Screen.propTypes = {
  currentCountry: PropTypes.string,
  error: PropTypes.string,
  handleSubmit: PropTypes.func,
  initialValues: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onClearAuthentication: PropTypes.func,
  onFocusField: PropTypes.func,
  setFieldError: PropTypes.func,
  resetFormState: PropTypes.func,
  loginType: PropTypes.string,
  passwordRef: PropTypes.object,
  signInRef: PropTypes.object,
  setStatus: PropTypes.bool,
  setSubmitting: PropTypes.bool,
  isValid: PropTypes.bool,
  validateForm: PropTypes.func,
  onAppleButtonClick: PropTypes.func,
  shakeButton: PropTypes.bool,
  setShakeButton: PropTypes.func,
  submitOnKeyUp: PropTypes.func,
  values: PropTypes.object,
  isQRCode: PropTypes.bool,
  enableLogin: PropTypes.bool,
  hasNextButton: PropTypes.bool,
  application: PropTypes.string,
  router: PropTypes.object,
  phoneNumberRef: PropTypes.object,
  t: PropTypes.func,
}

export default Screen
